import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { BoxType, Box } from "../utils/boxTypes";
import { RootState, AppDispatch } from "../app/store";
import axios from "axios";
import { handleLogoutUser } from "../utils/utility";
import { useNavigate } from "react-router-dom";

// Define the type for queryParams
interface QueryParams {
  offset: number;
  limit: number;
  order: string;
  q?: string; // Making 'q' property optional
  cat?: string;
  usd_enabled?: boolean;
  vpc_enabled?: boolean;
  is_physical_box?: boolean;
  is_credit_box?: boolean;
  is_digital_box?: boolean;
}

interface AllBoxesState {
  boxes: Box[];
  isLoading: boolean;
  isNeedToRefresh: boolean;
  currentPage: number;
  boxesPerPage: number;
  totalItems: number;
  searchString: string;
  giftCategoryName: string;
  giftCategoryMin: number;
  giftCategoryMax: number;

}

const initialState: AllBoxesState = {
  boxes: [],
  isLoading: false,
  isNeedToRefresh: false,
  currentPage: 1,
  boxesPerPage: 20,
  totalItems: 0,
  searchString: "",
  giftCategoryName: "",
  giftCategoryMin: 0,
  giftCategoryMax: 0,

};

interface FetchBoxesPayload {
  boxType: BoxType;
  isForGiftCategory: boolean;
}

// Thunk to fetch boxes from the API
export const fetchAllBoxes = createAsyncThunk<
  { boxType: string; boxes: any[]; total: number }, // Return type
  FetchBoxesPayload,                                // Argument type
  {
    dispatch: AppDispatch;                          // Dispatch type
    state: RootState;                               // State type
    rejectValue: string;                            // Optional reject value
  }
>(
  "boxes/fetchBoxes",
  async ({ boxType, isForGiftCategory }, { getState, dispatch, rejectWithValue }) => {
    const state = getState();

    if (!state.allBoxes) {
      console.error("allBoxes state is not defined");
      return rejectWithValue("allBoxes state is not defined");
    }

    const { currentPage, boxesPerPage, searchString, giftCategoryName, giftCategoryMin, giftCategoryMax } =
      state.allBoxes;
    const offset = (currentPage - 1) * boxesPerPage;

    const queryParams: QueryParams = {
      offset,
      limit: boxesPerPage,
      order: boxType,
    };

    if (isForGiftCategory) {
      if (giftCategoryName.length > 0) {
        // if (giftCategoryName === "Bronze Boxes") {
        //   queryParams.cat = "1-500";
        // } else if (giftCategoryName === "Silver Boxes") {
        //   queryParams.cat = "501-1000";
        // } else if (giftCategoryName === "Gold Boxes") {
        //   queryParams.cat = "1001-2500";
        // } else if (giftCategoryName === "Platinum Boxes") {
        //   queryParams.cat = "2501-5000";
        // } else if (giftCategoryName === "Diamond Boxes") {
        //   queryParams.cat = "5001-10000";
        // }
        // Ensure correct order even if min/max were swapped
  const lo = Math.min(giftCategoryMin, giftCategoryMax);
  const hi = Math.max(giftCategoryMin, giftCategoryMax);
  queryParams.cat = `${lo}-${hi}`;
      }
    } else {
      if (searchString.trim().length > 3) {
        queryParams.q = searchString.trim();
      }
    }

    try {
      const response = await axios.get("/boxes", {
        params: queryParams,
      });

      return { boxType, boxes: response.data.list, total: response.data.total };
    } catch (error: any) {
      // Check if the error is a 401 Unauthorized
      if (error.response && error.response.status === 401) {
        handleLogoutUser(dispatch, useNavigate());
      } else {
        console.error("Error fetching boxes:", error);
      }

      return rejectWithValue("Failed to fetch boxes");
    }
  }
);

// payload type
type GiftCategoryMeta = { name: string; min: number; max: number };


const allBoxesSlice = createSlice({
  name: "boxes",
  initialState,
  reducers: {
    setBoxesPerPage: (state, action: PayloadAction<number>) => {
      state.boxesPerPage = action.payload;
    },
    setCurrentPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    setSearchString: (state, action: PayloadAction<string>) => {
      state.searchString = action.payload;
    },
    // setGiftCategoryName: (state, action: PayloadAction<string>) => {
    //   state.giftCategoryName = action.payload;
    // },

      setGiftCategory(state, action: PayloadAction<GiftCategoryMeta>) {
      const { name, min, max } = action.payload;
      state.giftCategoryName = name;
      state.giftCategoryMin = min;
      state.giftCategoryMax = max;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchAllBoxes.fulfilled, (state, action) => {
      const { boxType, boxes, total } = action.payload || {
        boxType: BoxType,
        boxes: [],
        total: 0,
      };

      if (state.currentPage == 1) {
        state.boxes = boxes;
      } else if (boxes.length > 0) {
        state.boxes = [...state.boxes, ...boxes];
      }
      state.isLoading = false;
      state.totalItems = total;
    });

    builder.addCase(fetchAllBoxes.pending, (state, action) => {
      state.isLoading = true;
    });

    builder.addCase(fetchAllBoxes.rejected, (state, action) => {
      state.isLoading = false;
    });
  },
});

export const {
  setBoxesPerPage,
  setCurrentPage,
  setSearchString,
  setGiftCategory,
} = allBoxesSlice.actions;
export default allBoxesSlice.reducer;
